// ─────────────────────────────────────────────────────────────────────────────
// AHFC LETTERHEAD — shared header & footer for ALL generated Word documents.
//
// Single source of truth for company branding on generated .docx files
// (nurse assessments, supervisory visit reports, and any future documents).
// Mirrors the approved company template (AHFC_Template_4documents.docx):
//
//   • Header — logo + "A Heart For Care" + tagline, with a navy rule beneath.
//     Shown on the FIRST PAGE ONLY (via titlePage), per the template.
//   • Footer — company contact details (Tel / Fax / Email / Web) + page number,
//     shown on EVERY page. An optional doc-specific note can be added above it.
//
// Usage in a generator:
//   import { LETTERHEAD_SECTION_PROPS, buildHeaders, buildFooters } from './documents/letterhead';
//   const doc = new Document({
//     sections: [{
//       properties: LETTERHEAD_SECTION_PROPS,           // page size, margins, titlePage
//       headers: buildHeaders(),
//       footers: buildFooters({ note: 'AHFC GAPP Unified Assessment v3.0 · ...' }),
//       children,
//     }],
//   });
// ─────────────────────────────────────────────────────────────────────────────
import {
  Header, Footer, Paragraph, TextRun, ImageRun, PageNumber, Tab,
  Table, TableRow, TableCell,
  AlignmentType, WidthType, BorderStyle, TabStopType, convertInchesToTwip,
} from 'docx';
import { ahfcLogoBytes } from './ahfcLogo';
import { SUPPORT_EMAIL, WEBSITE_HOST } from '../org';

const twip = convertInchesToTwip;
const pt   = (n) => n * 2;            // points → half-points (docx run size unit)

// ── Brand palette (matches template) ─────────────────────────────────────────
const C_NAVY  = '1A3A5C';   // header company name, footer labels, header rule
const C_GREY  = '6B7280';   // tagline + footer values
const C_RULE  = 'D1D5DB';   // light grey footer top-rule
const FONT    = 'Calibri';

const NIL = { style: BorderStyle.NONE, size: 0, color: 'auto' };

// Default content width (twips). Generators pass their own to match body tables;
// 10800 == US Letter with 0.5" left/right margins (8.5" − 1.0" = 7.5").
const DEFAULT_CONTENT_W = 10800;

// ── Company contact details (edit here to update across every document) ───────
export const COMPANY = {
  name:    'A Heart For Care',
  tagline: 'Home Care Services by Hard Shoulder Group LLC',
  tel:     '470-470-6640',
  fax:     '404-537-6766',
  email:   SUPPORT_EMAIL,
  web:     WEBSITE_HOST,
};

// Page geometry shared by every letterhead document. titlePage:true lets the
// header render on the first page only (default header is left empty). The top
// margin reserves room for the 2-line header + logo; 0.5" sides keep the 7.5"
// (10800-twip) body-table width the generators already use.
export const LETTERHEAD_SECTION_PROPS = {
  page: {
    size:   { width: twip(8.5), height: twip(11) },
    margin: { top: twip(1.15), bottom: twip(0.95), left: twip(0.5), right: twip(0.5) },
  },
  titlePage: true,
};

// ── HEADER (first page only) ──────────────────────────────────────────────────
function headerParagraph(contentW) {
  return new Header({
    children: [
      new Table({
        width: { size: contentW, type: WidthType.DXA },
        columnWidths: [700, contentW - 700],
        borders: { top: NIL, bottom: NIL, left: NIL, right: NIL, insideH: NIL, insideV: NIL },
        rows: [
          new TableRow({
            children: [
              // Logo cell
              new TableCell({
                width: { size: 700, type: WidthType.DXA },
                borders: { top: NIL, bottom: NIL, left: NIL, right: NIL },
                margins: { right: 80 },
                verticalAlign: 'bottom',
                children: [new Paragraph({
                  spacing: { before: 0, after: 0 },
                  children: [new ImageRun({
                    type: 'png',
                    data: ahfcLogoBytes(),
                    transformation: { width: 32, height: 48 },  // ~0.33" × 0.5"
                    altText: { title: 'AHFC Logo', description: 'A Heart For Care Logo', name: 'AHFCLogo' },
                  })],
                })],
              }),
              // Name + tagline cell
              new TableCell({
                width: { size: contentW - 700, type: WidthType.DXA },
                borders: { top: NIL, bottom: NIL, left: NIL, right: NIL },
                margins: { left: 80 },
                verticalAlign: 'bottom',
                children: [
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { before: 0, after: 20 },
                    children: [new TextRun({ text: COMPANY.name, bold: true, color: C_NAVY, size: pt(13), font: FONT })],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { before: 0, after: 0 },
                    border: { bottom: { style: BorderStyle.SINGLE, color: C_NAVY, size: 6, space: 4 } },
                    children: [new TextRun({ text: COMPANY.tagline, italics: true, color: C_GREY, size: pt(8), font: FONT })],
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
      new Paragraph({ spacing: { before: 0, after: 0 }, children: [] }),
    ],
  });
}

// ── FOOTER (every page) ───────────────────────────────────────────────────────
function footerContent(note, contentW) {
  const lbl = (text) => new TextRun({ text, bold: true, color: C_NAVY, size: pt(8), font: FONT });
  const val = (text) => new TextRun({ text, color: C_GREY, size: pt(8), font: FONT });

  const children = [];

  // Optional document-specific note line (centred, above the contact rule).
  if (note) {
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 20 },
      children: [new TextRun({ text: note, italics: true, color: C_GREY, size: pt(7), font: FONT })],
    }));
  }

  // Contact line + right-aligned page number, with a light top rule.
  children.push(new Paragraph({
    tabStops: [{ type: TabStopType.RIGHT, position: contentW }],
    spacing: { before: 60, after: 0 },
    border: { top: { style: BorderStyle.SINGLE, color: C_RULE, size: 2, space: 4 } },
    children: [
      lbl('Tel: '),   val(`${COMPANY.tel}   |   `),
      lbl('Fax: '),   val(`${COMPANY.fax}   |   `),
      lbl('Email: '), val(`${COMPANY.email}   |   `),
      lbl('Web: '),   val(COMPANY.web),
      // Real tab element (not a "\t" char, which docx-js ignores) jumps to the
      // right tab stop so the page number aligns right while the rest stays left.
      new TextRun({ children: [new Tab(), 'Page '], color: C_GREY, size: pt(8), font: FONT }),
      new TextRun({ children: [PageNumber.CURRENT], color: C_GREY, size: pt(8), font: FONT }),
    ],
  }));

  return new Footer({ children });
}

// ── PUBLIC BUILDERS ───────────────────────────────────────────────────────────
// Header renders first page only; default (subsequent pages) is intentionally blank.
export function buildHeaders({ contentWidth = DEFAULT_CONTENT_W } = {}) {
  return {
    first:   headerParagraph(contentWidth),
    default: new Header({ children: [new Paragraph({ children: [] })] }),
  };
}

// Footer renders on every page. Pass { note } for an optional doc-specific line.
export function buildFooters({ note, contentWidth = DEFAULT_CONTENT_W } = {}) {
  return {
    first:   footerContent(note, contentWidth),
    default: footerContent(note, contentWidth),
  };
}
